// Importing React and external libs
import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Importing antd libs
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Select
} from 'antd';
import 'antd/es/button/style/css';
import 'antd/es/form/style/css';
import 'antd/es/input/style/css';
import 'antd/es/message/style/css';
import 'antd/es/modal/style/css';
import 'antd/es/select/style/css';

// Importing antd icons
import { LoadingOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';

// Importing components
import FailedLoginAttempts from './failedLoginAttempts';
import LockoutCountdown from './lockoutCountdown';
import MFAVerification from './mfa/verification';

// Importing helix hooks
import useLanguage from 'helix-hooks/language';

// Importing component languages
import languages from './lng';

// Importing component style
import './style.scss';

const { Item } = Form;
const { Option } = Select;

const Login = (props) => {
  // Loading component language
  const Language = useLanguage({ languages });

  const {
    bruteForceProtection,
    multiFactorAuthentication,
    multiFactorAuthenticationConfig: { onSendCode, onVerifyCode },
    regions,
    showForgotPassword,
    showRegionSelector,
    onRegionChange,
    onSendForgotPassword,
    onSignIn,
    onSignedIn,
  } = props;
  const [form] = Form.useForm();

  const [isUserLocked, setIsUserLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [loginAttemptResponse, setLoginAttemptResponse] = useState(null);

  const forgotPasswordFormRef = useRef();
  const usernameRef = useRef();

  useEffect(() => {
    if (usernameRef.current) usernameRef.current.focus();
  }, []);

  return (
    <div className="login-wrapper">
      <Form
        className="login-form"
        disabled={isUserLocked}
        form={form}
        name="login-form"
        onFinish={_onSignIn}
      >
        {multiFactorAuthentication && showMFAVerification
          ? (
            <MFAVerification
              loginAttemptResponse={loginAttemptResponse}
              onSendCode={onSendCode}
              onVerifyCode={onVerifyCode}
            />
          )
          : (
            <Fragment>
              {bruteForceProtection && (
                <FailedLoginAttempts loginAttemptResponse={loginAttemptResponse} />
              )}
              {showRegionSelector && (
                <Item initialValue={regions[0].name} name="regions">
                  <Select onChange={_onRegionChange} disabled={isUserLocked}>
                    {regions.map(({ name }) => (
                      <Option key={name}>{Language.get('login', name)}</Option>
                    ))}
                  </Select>
                </Item>
              )}
              <Item
                name="username"
                rules={[{ message: Language.get('login', 'USERNAME_IS_REQUIRED'), required: true }]}
              >
                <Input
                  disabled={loading || isUserLocked}
                  prefix={<UserOutlined />}
                  placeholder={Language.get('login', 'USERNAME')}
                  ref={usernameRef}
                />
              </Item>
              <Item
                name="password"
                rules={[{ message: `${Language.get('login', 'PASSWORD_IS_REQUIRED')}.`, required: true }]}
              >
                <Input
                  disabled={loading || isUserLocked}
                  prefix={<LockOutlined />}
                  type="password"
                  placeholder={Language.get('login', 'PASSWORD')}
                  onPressEnter={() => form.submit()}
                />
              </Item>
              {bruteForceProtection && (
                <LockoutCountdown loginAttemptResponse={loginAttemptResponse} onCountdownEnd={() => setIsUserLocked(false)} />
              )}
              <Item className="form-actions">
                <Button disabled={loading || isUserLocked} htmlType="submit" key="submit" type="primary">
                  {loading && (<LoadingOutlined />)}
                  {Language.get('login', 'LOGIN')}
                </Button>
                {showForgotPassword && (
                  <Button disabled={isUserLocked} type="link" onClick={() => setShowForgotPasswordModal(true)}>
                    {Language.get('login', 'FORGOT_PASSWORD')}
                  </Button>
                )}
              </Item>
            </Fragment>
          )
        }
      </Form>
      <Modal
        cancelText={Language.get('common', 'CANCEL')}
        okText={Language.get('login', 'SEND')}
        open={showForgotPasswordModal}
        title={Language.get('login', 'FORGOT_PASSWORD')}
        onOk={_onSendForgotPassword}
        onCancel={() => {
          forgotPasswordFormRef.current.resetFields();
          setShowForgotPasswordModal(false);
        }}
      >
        <p>{Language.get('login', 'FORGOT_PASSWORD_TEXT')}</p>
        <Form name="forgot-password-form" ref={forgotPasswordFormRef}>
          <Item
            name="email"
            rules={[{
              message: Language.get('login', 'NOT_VALID_EMAIL'),
              required: true,
              type: 'email',
            }]}
          >
            <Input placeholder={Language.get('login', 'EMAIL')} />
          </Item>
        </Form>
      </Modal>
    </div>
  );

  /**
   * On region change
   * @param {void}
   * @return {void}
  **/
  async function _onRegionChange(region) {
    setLoading(true);
    const res = await onRegionChange(region);
    if (res.error) message.error(Language.get('login', res.msg));
    setLoading(false);
  }

  /**
   * On click sign in action
   * @param {void}
   * @return {void} message error if any
  **/
  async function _onSignIn(credentials) {
    setLoading(true);
    const res = await onSignIn(credentials);
    setLoginAttemptResponse(res);

    if (res.error) {
      const isLocked = res?.['is_locked'];
      setIsUserLocked(!!isLocked);
      message.error(Language.get('login', res.msg));
    } else {
      // Sign in succeded
      form.resetFields();
      if (res?.['is_mfa_required']) {
        setShowMFAVerification(true);
      } else {
        await onSignedIn(credentials);
      }
    }
    setLoading(false);
  }

  /**
   * On modal send button clicked
   * @param {void}
   * @return {void} state change
  **/
  async function _onSendForgotPassword() {
    const { resetFields, validateFields } = forgotPasswordFormRef.current;
    try {
      const values = await validateFields();
      // it is a valid email
      const res = await onSendForgotPassword(values.email);
      if (res.error) return message.error(Language.get('login', res.msg));
      resetFields();
      message.success(Language.get('login', 'SEND_FORGOT_PASSWORD'));
      setShowForgotPasswordModal(false);
    } catch (error) {
      console.log(error);
      return;
    }
  }
};

Login.propTypes = {
  bruteForceProtection: PropTypes.bool,
  multiFactorAuthentication: PropTypes.bool,
  multiFactorAuthenticationConfig: PropTypes.object,
  regions: PropTypes.array,
  showForgotPassword: PropTypes.bool,
  showRegionSelector: PropTypes.bool,
  onRegionChange: PropTypes.func,
  onSendForgotPassword: PropTypes.func,
  onSignIn: PropTypes.func,
  onSignedIn: PropTypes.func,
};

Login.defaultProps = {
  bruteForceProtection: false,
  multiFactorAuthentication: false,
  multiFactorAuthenticationConfig: {
    onSendCode: () => { console.log('send code'); },
    onVerifyCode: () => { console.log('verify code'); },
  },
  regions: [{ name: 'current' }],
  showForgotPassword: true,
  showRegionSelector: true,
  onRegionChange: (region) => { console.log(region); return { error: false }; },
  onSendForgotPassword: (email) => { console.log(email); return { error: false }; },
  onSignIn: (credentials) => { console.log(credentials); return { error: false }; },
  onSignedIn: (response) => { console.log(response); return { error: false }; },
};

export default Login;
